import React, { useState } from 'react';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, CaptionLight, Subtitle1Bold } from '../../lib/font';
import { useMemberCheckinHistory } from '@/features/checkin/hooks';
import dayjs from 'dayjs';

interface Props {
    isVisible: boolean;
    onClose: () => void;
    centerId: string;
    memberId: string;
    memberName: string;
}

const MemberCheckinHistoryModal = ({ isVisible, onClose, centerId, memberId, memberName }: Props) => {
    const [page, setPage] = useState(1);

    const { data } = useMemberCheckinHistory({
        centerId,
        memberId,
        page,
        limit: 15,
        enabled: isVisible && !!memberId,
    });

    const logs = data?.results ?? [];
    const totalResults = data?.totalResults ?? 0;
    const totalPages = data?.totalPages ?? 1;

    if (!isVisible) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
            }}
            onClick={onClose}
        >
            <Col
                style={{
                    backgroundColor: '#fff',
                    borderRadius: 16,
                    padding: 28,
                    width: 560,
                    maxHeight: '80vh',
                    position: 'relative',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 닫기 */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute', top: 16, right: 16,
                        background: 'none', border: 'none',
                        fontSize: 20, color: '#9CA3AF', cursor: 'pointer',
                        lineHeight: 1, padding: 4,
                    }}
                >
                    ✕
                </button>

                {/* 헤더 */}
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <Subtitle1Bold>{memberName}</Subtitle1Bold>
                    <CaptionLight color={colors.LAVEL_1}>출석 이력</CaptionLight>
                </Row>

                <Row style={{ marginTop: 8, gap: 12 }}>
                    <Body3Regular style={{ color: colors.LAVEL_1 }}>
                        총 {totalResults}회 출석
                    </Body3Regular>
                </Row>

                {/* 리스트 */}
                <Col style={{ marginTop: 16, overflowY: 'auto', maxHeight: 'calc(80vh - 180px)' }}>
                    {logs.length === 0 ? (
                        <Col style={{ padding: 40, alignItems: 'center' }}>
                            <CaptionLight color={colors.LAVEL_1}>출석 이력이 없습니다</CaptionLight>
                        </Col>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                                    <th style={thStyle}>날짜</th>
                                    <th style={thStyle}>체크인</th>
                                    <th style={thStyle}>체크아웃</th>
                                    <th style={thStyle}>방법</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log: any) => (
                                    <tr key={log._id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={tdStyle}>{dayjs(log.checkinAt).format('MM/DD (ddd)')}</td>
                                        <td style={tdStyle}>{dayjs(log.checkinAt).format('HH:mm')}</td>
                                        <td style={tdStyle}>
                                            {log.checkoutAt ? (
                                                <span>{dayjs(log.checkoutAt).format('HH:mm')}</span>
                                            ) : (
                                                <span style={{ color: '#16A34A', fontWeight: 500 }}>이용중</span>
                                            )}
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{
                                                fontSize: 11,
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                backgroundColor:
                                                    log.method === 'kiosk_phone' ? '#DBEAFE'
                                                    : log.method === 'qr' ? '#D1FAE5'
                                                    : '#F3F4F6',
                                                color:
                                                    log.method === 'kiosk_phone' ? '#1E40AF'
                                                    : log.method === 'qr' ? '#065F46'
                                                    : '#4B5563',
                                            }}>
                                                {METHOD_LABELS[log.method] || log.method}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Col>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                    <Row style={{ marginTop: 16, justifyContent: 'center', gap: 4 }}>
                        <PageBtn label="‹" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} />
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let n: number;
                            if (totalPages <= 5) n = i + 1;
                            else if (page <= 3) n = i + 1;
                            else if (page >= totalPages - 2) n = totalPages - 4 + i;
                            else n = page - 2 + i;
                            const sel = n === page;
                            return (
                                <button
                                    key={n}
                                    style={{
                                        width: 28, height: 28, borderRadius: 4, fontSize: 12,
                                        fontWeight: sel ? 700 : 400,
                                        backgroundColor: sel ? '#3B82F6' : '#fff',
                                        color: sel ? '#fff' : '#374151',
                                        border: sel ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setPage(n)}
                                >
                                    {n}
                                </button>
                            );
                        })}
                        <PageBtn label="›" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} />
                    </Row>
                )}
            </Col>
        </div>
    );
};

const PageBtn = ({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) => (
    <button
        style={{
            padding: '4px 8px', borderRadius: 4, fontSize: 12,
            border: '1px solid #E5E7EB', backgroundColor: '#fff',
            color: disabled ? '#D1D5DB' : '#374151',
            cursor: disabled ? 'default' : 'pointer',
        }}
        disabled={disabled}
        onClick={onClick}
    >
        {label}
    </button>
);

const thStyle: React.CSSProperties = { padding: '8px 12px', fontSize: 12, fontWeight: 500, color: '#6B7280', textAlign: 'center' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: 13, textAlign: 'center', color: '#374151' };

const METHOD_LABELS: Record<string, string> = {
    kiosk_phone: '키오스크',
    qr: 'QR',
    manual: '수동',
};

export default MemberCheckinHistoryModal;
